import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Instagram, Facebook, Linkedin } from 'lucide-react';
import silaLogo from '@/assets/brand/sila-logo.png';
import { ISTITUZIONALI } from '@/lib/institutionalLogos';

const AREE = [
  { label: 'Alloggi', to: '/alloggi' },
  { label: 'Ristoranti', to: '/dove-mangiare' },
  { label: 'Experiences', to: '/esperienze' },
  { label: 'Educational', to: '/educational/in-sila' },
  { label: 'Eventi', to: '/eventi' },
  { label: 'Prodotti Locali', to: '/shop' },
];

const ESPLORA = [
  { label: 'FAQ', to: '/faq', testid: 'footer-faq' },
  { label: 'Magazine', to: '/blog', testid: 'footer-blog' },
  { label: 'Chi sono', to: '/chi-sono', testid: 'footer-chi-sono' },
  { label: 'Il progetto', to: '/il-progetto', testid: 'footer-progetto' },
  { label: 'Contatti', to: '/contatti', testid: 'footer-contatti' },
];

const AZIENDA = [
  { label: 'Diventa Stakeholder', to: '/register', testid: 'footer-diventa-stakeholder' },
  { label: 'Diventa Partner', to: '/diventa-partner', testid: 'footer-diventa-partner' },
  { label: 'Lavora con noi', to: '/lavora-con-noi', testid: 'footer-lavora' },
];

// Loghi istituzionali/riconoscimenti: sorgente condivisa in @/lib/institutionalLogos.

export const PublicFooter = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-border bg-card" data-testid="public-footer">
      <div className="sila-section grid grid-cols-2 gap-8 py-14 md:grid-cols-4 lg:grid-cols-6 lg:gap-10">
        {/* Brand */}
        <div className="col-span-2 md:col-span-4 lg:col-span-2">
          <Link to="/" className="inline-flex items-center" aria-label="Sila Experiences — home" data-testid="footer-logo">
            <img
              src={silaLogo}
              alt="Sila Experiences — Riscopri la tua natura attraverso la Natura della Sila"
              className="h-14 w-auto dark:rounded-md dark:bg-white/95 dark:px-2 dark:py-1"
              width="260"
              height="133"
            />
          </Link>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">{t('footer.tagline')}</p>
          <div className="mt-4 flex items-center gap-3 text-muted-foreground">
            <a href="#" aria-label="Instagram" className="transition-colors hover:text-primary"><Instagram className="h-5 w-5" /></a>
            <a href="#" aria-label="Facebook" className="transition-colors hover:text-primary"><Facebook className="h-5 w-5" /></a>
            <a href="#" aria-label="LinkedIn" className="transition-colors hover:text-primary"><Linkedin className="h-5 w-5" /></a>
          </div>
        </div>

        {/* Le Aree */}
        <div data-testid="footer-col-aree">
          <h4 className="mb-3 text-sm font-semibold">Le Aree Sila Experiences</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {AREE.map((a) => (
              <li key={a.to}><Link to={a.to} className="transition-colors hover:text-foreground">{a.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Esplora */}
        <div data-testid="footer-col-esplora">
          <h4 className="mb-3 text-sm font-semibold">{t('footer.explore')}</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {ESPLORA.map((e) => (
              <li key={e.to}><Link to={e.to} data-testid={e.testid} className="transition-colors hover:text-foreground">{e.label}</Link></li>
            ))}
            <li>
              <a href="https://synentiaconsulting.com" target="_blank" rel="noopener noreferrer" data-testid="footer-synentia" className="transition-colors hover:text-foreground">Synentia</a>
            </li>
          </ul>
        </div>

        {/* Azienda */}
        <div data-testid="footer-col-azienda">
          <h4 className="mb-3 text-sm font-semibold">Azienda</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {AZIENDA.map((a) => (
              <li key={a.label}><Link to={a.to} data-testid={a.testid} className="transition-colors hover:text-foreground">{a.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Legale */}
        <div data-testid="footer-col-legale">
          <h4 className="mb-3 text-sm font-semibold">{t('footer.legal')}</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/privacy-sicurezza" className="transition-colors hover:text-foreground" data-testid="footer-privacy-sicurezza">Privacy &amp; Sicurezza</Link></li>
            <li><Link to="/termini" className="transition-colors hover:text-foreground" data-testid="footer-terms">Termini e Condizioni</Link></li>
            <li>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-preferences'))}
                className="text-left transition-colors hover:text-foreground"
                data-testid="footer-cookie-prefs"
              >Preferenze cookie</button>
            </li>
          </ul>
          <div className="mt-4 space-y-1.5 text-sm leading-relaxed text-muted-foreground">
            <p className="font-medium text-foreground">Synentia LLC</p>
            <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />30 N. Gould St. Ste R,<br />Sheridan, WY 82801 (USA)</p>
            <p className="text-xs">EIN: 30-1497540</p>
            <p className="flex items-center gap-2"><Mail className="h-4 w-4 shrink-0" /><a href="mailto:legal@synentiaconsulting.com" className="transition-colors hover:text-foreground break-all">legal@synentiaconsulting.com</a></p>
          </div>
        </div>
      </div>

      {/* Riconoscimenti / Partner istituzionali */}
      <div className="border-t border-border">
        <div className="sila-section py-8">
          <h4 className="mb-5 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Riconoscimenti e Partner Istituzionali</h4>
          <ul className="mx-auto grid max-w-3xl grid-cols-2 items-stretch gap-3 sm:grid-cols-4 sm:gap-4" data-testid="footer-istituzionali">
            {ISTITUZIONALI.map((it) => (
              <li key={it.slug}>
                <a
                  href={it.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={it.name}
                  title={it.name}
                  data-testid={`footer-logo-${it.slug}`}
                  className="flex h-20 items-center justify-center rounded-xl border border-border bg-white p-3 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:h-24"
                >
                  <picture className="flex h-full w-full items-center justify-center">
                    <source srcSet={`/istituzionali/${it.slug}.webp`} type="image/webp" />
                    <img
                      src={`/istituzionali/${it.slug}.png`}
                      alt={it.name}
                      loading="lazy"
                      decoding="async"
                      className="max-h-full max-w-full object-contain"
                    />
                  </picture>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="sila-section flex flex-col items-center justify-between gap-2 py-5 text-center text-xs text-muted-foreground sm:flex-row sm:text-left">
          <p>© {year} Sila Experiences · Synentia LLC. {t('footer.rights')}</p>
          <p>Made with care in Calabria 🌲</p>
        </div>
      </div>
    </footer>
  );
};
